import cloneDeep from "lodash/cloneDeep";
import { defineStore } from "pinia";
import { ref } from "vue";
import { IndexedDB } from "@/indexeddb";
import { type Acl, Category, Topic, type User } from "@/model";
import AclManager from "@/model/acl-manager";
import { useAppStore } from "./app-store";
import { useCategoryStore } from "./category-store";
import TopicDao from "./topic-dao";
import { useUserStore } from "./user-store";

type Topics = {
  [domainId: string]: Topic[];
};

export type TopicUpdateMessagesPayload = {
  domainId: string;
  topicId: string;
  messageCount: number;
};

function normalize(topic: Topic, domainId: string): string {
  Topic.normalize(topic, domainId);
  return topic.domainId;
}

export const useTopicStore = defineStore("topics", () => {
  const topics = ref<Topics>({});
  const totalCount = ref<{ [domainId: string]: number }>({}); // 組織毎の全話題数

  async function setIndexedDB(): Promise<void> {
    await Promise.all(
      Object.keys(topics.value).map(async (key) => {
        await IndexedDB.set("TOPIC", key, topics.value[key]);
      }),
    );
  }

  const getDomainTopics = (domainId: string): Topic[] => {
    if (!topics.value[domainId]) {
      topics.value = { ...topics.value, [domainId]: [] };
    }
    return topics.value[domainId];
  };

  function addOrUpdateTopic(topic: Topic) {
    normalize(topic, topic.domainId);
    const domainTopics = getDomainTopics(topic.domainId);
    const index = domainTopics.findIndex((t) => t.id === topic.id);
    if (index < 0) {
      domainTopics.push(topic);
    } else {
      domainTopics.splice(index, 1, topic);
    }
  }

  function deleteTopicInternal(domainId: string, id: string) {
    const domainTopics = getDomainTopics(domainId);
    const index = domainTopics.findIndex((t) => t.id === id);
    if (index < 0) {
      // 何もしない
    } else {
      domainTopics.splice(index, 1); // 削除
    }
  }

  // --- getters ---
  const get = (domainId: string, exists = true): Topic[] => {
    const list = topics.value[domainId] || [];
    return exists ? list.filter((t) => Topic.isExists(t)) : list;
  };

  const getOne = (
    domainId: string,
    topicId: string,
    _exists = false,
  ): Topic | undefined => {
    domainId = domainId || useAppStore().domainId;
    const domainTopics = topics.value[domainId] || [];
    return domainTopics.find((topic) => topic.id === topicId);
  };

  const getLength = (
    domainId: string,
    exists = false,
    _ignoreAcl = false,
  ): number => {
    const list = get(domainId, exists) as Topic[] | undefined;
    return list ? list.length : 0;
  };

  const getAllTopicsLength = (domainId: string): number | undefined => {
    return totalCount.value[domainId];
  };

  // --- 内部更新 (旧 mutations 相当) ---
  const addMutation = (payload: { data: Topic | Topic[] }): void => {
    if (Array.isArray(payload.data)) {
      (payload.data || []).forEach((topic) => {
        addOrUpdateTopic(topic);
      });
    } else {
      addOrUpdateTopic(payload.data);
    }
  };

  const overwriteMutation = (payload: {
    data: Topic[];
    domainId: string;
  }): void => {
    topics.value = { ...topics.value, [payload.domainId]: payload.data };
  };

  const updateMutation = (payload: { data: Topic }): void => {
    addOrUpdateTopic(payload.data);
  };

  const deleteMutation = (payload: {
    domainId: string;
    topicId: string;
  }): void => {
    deleteTopicInternal(payload.domainId, payload.topicId);
  };

  const setTotalCountMutation = (payload: {
    domainId: string;
    count: number;
  }): void => {
    totalCount.value = {
      ...totalCount.value,
      [payload.domainId]: payload.count,
    };
  };

  // --- actions ---
  const create = async (payload: {
    topic: Topic;
    notifyOnCreate: boolean;
  }): Promise<void> => {
    const topic = payload.topic;
    if (Topic.isTemporary(topic) === false) return undefined;
    normalize(topic, useAppStore().domainId);

    const domainId = topic.domainId;
    const register = await TopicDao.create(topic, payload.notifyOnCreate);
    if (register) {
      addMutation({ data: register });
      await setIndexedDB();

      // 同期
      await fetch({ domainId: domainId });
    }
  };

  /** Topicの更新 */
  const update = async (payload: { param: Partial<Topic> }): Promise<void> => {
    const param = payload.param;
    const topic = getOne(param.domainId as string, param.id as string);
    if (!topic) {
      console.error("topic is not found:", param.domainId, param.id);
      return;
    }
    const updated = cloneDeep(topic);
    updated.copyFrom(param);

    const register = await TopicDao.update(updated);
    if (register) {
      updateMutation({ data: register });
      await setIndexedDB();
    }
  };

  /**
   * topicを追加する
   */
  const add = async (payload: {
    topic: Topic;
    domainId?: string;
  }): Promise<void> => {
    normalize(payload.topic, useAppStore().domainId);
    const topic = payload.topic;
    const _domainId = payload.topic.domainId;

    // カテゴリー側の登録作業
    const category = topic.category;
    if (Category.isTemporary(category)) {
      const result = await useCategoryStore().create({
        data: category,
      });
      if (result) topic.category = result; // 作成したもので差し替え
    }
    addMutation({ data: topic });
    await setIndexedDB();
  };

  /**
   * サーバ側から組織のTopicを取得する
   */
  const fetch = async (payload: {
    domainId: string;
    topicId?: string;
  }): Promise<void> => {
    const domainId = payload.domainId;
    const categories = useCategoryStore().get(domainId) || [];

    const results = await TopicDao.read(categories, domainId);
    if (results) {
      const list = results.topics;
      setTotalCountMutation({ domainId: domainId, count: results.totalCount });
      overwriteMutation({ data: list, domainId: domainId });
      await setIndexedDB();
    }
  };

  /**
   * Topicを削除する
   */
  const deleteTopic = async (payload: {
    topicId: string;
    param: { deleted: true; deletedUser: string };
  }): Promise<void> => {
    const domainId = useAppStore().domainId;
    const topicId = payload.topicId;
    const list = get(domainId) as Topic[] | undefined;
    if (!list) return;
    const topic = list.find((t) => t.id === topicId);
    if (!topic) return;

    const updated = cloneDeep(topic);
    updated.deleted = payload.param.deleted;
    updated.deletedUser = payload.param.deletedUser;
    const register = await TopicDao.update(updated);
    if (register) {
      deleteMutation({ domainId, topicId });
      await setIndexedDB();
    }
  };

  /**
   * 再更新要求
   * このメソッド自体は何もしないので subscribAction 側が対処してください
   */
  const invalidate = async (_payload: {
    domainId: string;
    topicId: string;
    newAcl?: Acl;
    oldAcl?: Acl;
  }): Promise<void> => {
    // Nop — $onAction サブスクライバーがペイロードを参照する
  };

  const registerSubscription = async (payload: {
    domainId: string;
    me: User;
  }): Promise<void> => {
    const domainId = payload.domainId;
    try {
      await TopicDao.registerSubscrptions(domainId, {
        onCreate: (domainId: string, topicId: string) => {
          fetch({ domainId, topicId });
        },
        onUpdate: (
          domainId: string,
          topicId: string,
          deleted?: boolean | null,
          _deletedUser?: string | null,
          newAcl?: Acl,
        ) => {
          if (deleted) {
            deleteMutation({ domainId, topicId });
          } else {
            const me = useUserStore().me as User;
            const oldTopic = getOne(domainId, topicId) as Topic | undefined;
            const _oldAcl = oldTopic?.acl;
            if (AclManager.readTopic(me, { domainId, acl: newAcl })) {
              fetch({ domainId, topicId }).then(() => {
                useTopicStore().invalidate({
                  domainId,
                  topicId,
                  newAcl,
                  oldAcl: _oldAcl,
                });
              });
            } else {
              deleteMutation({ domainId, topicId });
            }
          }
        },
        onDelete: (domainId: string, topicId: string) => {
          deleteMutation({ domainId, topicId });
        },
      });
    } catch (error) {
      console.error("Topic登録エラー:%O", error);
    }
  };

  const removeSubscription = async (payload: {
    domainId: string;
  }): Promise<void> => {
    await TopicDao.closeSubscription(payload.domainId);
  };

  const restore = async (): Promise<void> => {
    const restored = await IndexedDB.getAll("TOPIC");
    if (restored) {
      topics.value = restored;
    }
  };

  return {
    topics,
    totalCount,
    get,
    getOne,
    getLength,
    getAllTopicsLength,
    create,
    update,
    add,
    fetch,
    delete: deleteTopic,
    invalidate,
    registerSubscription,
    removeSubscription,
    restore,
  };
});

export default useTopicStore;
